import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  View,
  Text,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import {Colors} from '../../constants/Colors';
import ProductsService from '../../services/ProductsService/ProductsService';
import {useBagStore} from '../../store/BagStore';
import {useUserStore} from '../../store/UserStore';
import ProductList from '../SugestedProducts/ProductList/ProductList';
import {styles} from './styles';

interface FeaturedProductsCarouselProps {
  handleAddBag: (product: any, quantity: number) => void;
  openProduct: (product: any) => void;
}

const FeaturedProductsCarousel: React.FC<FeaturedProductsCarouselProps> = ({
  handleAddBag,
  openProduct,
}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const {user} = useUserStore();
  const {products: bagProducts} = useBagStore();
  const bagProductsRef = useRef(bagProducts);
  bagProductsRef.current = bagProducts;

  const fetchFeaturedProducts = useCallback(async () => {
    if (!user?.clientCode || !user?.storeAccess) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await ProductsService.featuredProductsGet(
        user.clientCode,
        10, // Limitar a 10 produtos para o carrossel
        1,
      );

      if (response.data && Array.isArray(response.data)) {
        const featuredProducts = response.data.filter((item: any) => {
          return item.sku && item.estoque > 0;
        });

        // Filtra produtos já no carrinho usando a ref para não recriar o callback
        const productsNotInBag = featuredProducts.filter(
          (featuredProduct: any) =>
            !bagProductsRef.current.some(
              (bagProduct: any) => bagProduct.sku === featuredProduct.sku,
            ),
        );

        setData(productsNotInBag);
      }
    } catch (error) {
      console.error('Error fetching featured products for carousel:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [user?.clientCode, user?.storeAccess]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, [fetchFeaturedProducts]);

  // Reagir quando produtos são adicionados/removidos do carrinho
  useEffect(() => {
    if (data.length > 0) {
      // Filtrar produtos que ainda não estão no carrinho
      const updatedData = data.filter(
        (featuredProduct: any) =>
          !bagProducts.some(
            (bagProduct: any) => bagProduct.sku === featuredProduct.sku,
          ),
      );
      
      // Atualizar apenas se houver mudança
      if (updatedData.length !== data.length) {
        setData(updatedData);
      }
    }
  }, [bagProducts]);

  if (loading && data.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={Colors.RedPallete.red} />
      </View>
    );
  }

  // Não mostrar se não houver produtos
  if (!data || data.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>🔥 Produtos Imperdíveis</Text>
      </View>
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        data={data}
        contentContainerStyle={{
          paddingHorizontal: 12,
        }}
        renderItem={({item}) => (
          <ProductList
            item={item}
            image={item.image}
            title={item.custom_title || item.descricao}
            description={
              (item.custom_title || item.descricao) +
              ' ' +
              (item.complemento || '').replace('- R$', '').replace(/\\n/g, '')
            }
            AddBag={(quantity: number) => handleAddBag(item, quantity)}
            onPress={() => openProduct(item)}
          />
        )}
        keyExtractor={(item, index) => `featured-${item.sku}-${index}`}
      />
    </View>
  );
};

export default FeaturedProductsCarousel;
